import { styled, useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import MuiToolbar from '@mui/material/Toolbar';
import MuiAppbar from '@mui/material/AppBar';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { tabsClasses } from '@mui/material/Tabs';
import { IconButton, useMediaQuery } from '@mui/material';
import PortalLogo from '../../components/PortalLogo';
import UserMenu from './UserMenu';
import ColorModeIconDropdown from './ColorMode';
import ImpersonationBanner from '../../modules/Auth/components/ImpersonationBanner';
import { useAppSelector } from '../../redux/store';

const AppBar = styled(MuiAppbar)(({ theme }) => ({
	transition: theme.transitions.create(['margin', 'width'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	backgroundColor: theme.palette.background.paper, // match SideNav
	color: theme.palette.text.primary,
	boxShadow: 'none', // flat modern
	borderBottom: `1px solid ${theme.palette.divider}`, // subtle separation
}));

const Toolbar = styled(MuiToolbar)(({ theme }) => ({
	width: '100%',
	minHeight: 56, // reduced height
	padding: theme.spacing(0, 2),
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: theme.spacing(1),
	flexShrink: 0,
	[`& ${tabsClasses.flexContainer}`]: {
		gap: theme.spacing(1),
		p: theme.spacing(1),
		pb: 0,
	},
}));

export default function AppNavbar({
	open,
	setOpen,
}: {
	open?: boolean;
	setOpen?: (val: boolean) => void;
}) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { impersonating } = useAppSelector(s => s.auth);

	return (
		<>
			{/* ✅ Banner sits ABOVE the AppBar */}
			<ImpersonationBanner />

			{/* ✅ Toolbar shifts DOWN by banner height */}
			<AppBar
				position="fixed"
				sx={{
					top: impersonating ? 24 : 0,
					zIndex: theme => theme.zIndex.drawer + 1,
				}}
			>
				<Toolbar>
					{/* Left side */}
					<Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexGrow: 1 }}>
						<IconButton
							aria-label="open drawer"
							onClick={() => setOpen?.(!open)}
							edge="start"
							size="small"
							sx={{ mr: 1, ...(!isMobile && { display: 'none' }) }}
						>
							{open ? <CloseIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
						</IconButton>
						<PortalLogo height={32} />
					</Stack>

					{/* Right side */}
					<Stack direction="row" spacing={1} alignItems="center">
						<ColorModeIconDropdown />
						<IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
							<NotificationsIcon fontSize="small" />
						</IconButton>
						<UserMenu />
					</Stack>
				</Toolbar>
			</AppBar>
		</>
	);
}
